<template>
  <div class="rounded-xl border border-slate-200 bg-white/80 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
    <!-- Header -->
    <div class="border-b border-slate-200 p-4 dark:border-slate-700 sm:p-5">
      <div class="flex items-center justify-between">
        <h3 class="flex items-center gap-2.5 text-xl font-semibold text-slate-900 dark:text-white">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-violet-500/85 dark:text-violet-300/80"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path
              fill-rule="evenodd"
              d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
              clip-rule="evenodd"
            />
          </svg>
          {{ t('app.views.cashRegister.cutsHistory') }}
        </h3>
        <button
          :disabled="loading"
          class="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          @click="loadCuts"
        >
          {{ loading ? 'Cargando...' : (isExpanded ? 'Ocultar' : 'Ver historial') }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <LoadingSpinner
      v-if="loading"
      size="md"
      color="primary"
      text="Cargando cortes..."
    />

    <!-- Error State -->
    <div
      v-else-if="error"
      class="p-6 text-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-12 w-12 mx-auto text-red-400 mb-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p class="text-sm text-red-600 dark:text-red-400">
        {{ error }}
      </p>
    </div>

    <!-- Empty State -->
    <EmptyState
      v-else-if="isExpanded && cuts.length === 0"
      icon="document"
      :title="t('app.views.cashRegister.noCuts')"
      variant="minimal"
      size="md"
    />

    <!-- Cuts List -->
    <div
      v-else-if="isExpanded && cuts.length > 0"
      class="divide-y divide-slate-200 dark:divide-slate-700"
    >
      <div
        v-for="(cut, index) in cuts"
        :key="cut.id"
        class="p-4 transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/30 sm:p-5"
      >
        <div class="space-y-3.5">
          <!-- Cut header row -->
          <div class="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex flex-wrap items-center gap-2.5">
              <span class="text-lg font-semibold leading-none text-violet-600 dark:text-violet-300">
                {{ t('app.views.cashRegister.cutNumber', { number: cuts.length - index }) }}
              </span>
              <span class="text-sm text-slate-500 dark:text-slate-400">
                {{ formatDate(cut.generated_at) }}
              </span>
            </div>

            <!-- View Details Button -->
            <button
              class="self-start rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-violet-600 transition-colors hover:bg-violet-50 dark:border-slate-600 dark:text-violet-300 dark:hover:bg-violet-900/20"
              @click="viewCutDetails(cut)"
            >
              {{ t('app.views.cashRegister.viewCutDetails') }}
            </button>
          </div>

          <!-- Cut Summary -->
          <div class="grid grid-cols-1 gap-2.5 text-sm sm:grid-cols-2 lg:grid-cols-4 sm:gap-3">
            <div class="min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/40">
              <p class="text-xs uppercase tracking-wide leading-snug text-slate-500 dark:text-slate-400">
                {{ t('app.views.cashRegister.totalSales') }}
              </p>
              <p class="mt-1 font-semibold text-slate-900 dark:text-slate-100">
                {{ formatCurrency(getCutData(cut).total_sales) }}
              </p>
            </div>
            <div class="min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/40">
              <p class="text-xs uppercase tracking-wide leading-snug text-slate-500 dark:text-slate-400">
                {{ t('app.views.cashRegister.totalExpenses') }}
              </p>
              <p class="mt-1 font-semibold text-slate-900 dark:text-slate-100">
                {{ formatCurrency(getCutData(cut).total_expenses) }}
              </p>
            </div>
            <div class="min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/40">
              <p class="text-xs uppercase tracking-wide leading-snug text-slate-500 dark:text-slate-400">
                {{ t('app.views.cashRegister.transactions') }}
              </p>
              <p class="mt-1 font-semibold text-slate-900 dark:text-slate-100">
                {{ getCutData(cut).total_transactions }}
              </p>
            </div>
            <div class="min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800/40">
              <p class="text-xs uppercase tracking-wide leading-snug text-slate-500 dark:text-slate-400">
                {{ t('app.views.cashRegister.netCashFlow') }}
              </p>
              <p
                class="mt-1 font-semibold"
                :class="getCutData(cut).net_cash_flow < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'"
              >
                {{ formatCurrency(getCutData(cut).net_cash_flow) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cut Details Modal -->
    <Teleport to="body">
      <BaseModal
        v-if="selectedCut"
        size="md"
        :title="t('app.views.cashRegister.cutDetails')"
        @close="selectedCut = null"
      >
        <CutDetails 
          :cut-data="getCutData(selectedCut)" 
          :is-loading="false"
          :show-border="false"
        />
      </BaseModal>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import cashRegisterService from '@/services/cashRegisterService';
import { formatCurrency } from '@/utils/priceHelpers';
import CutDetails from '@/components/cashRegister/CutDetails.vue';
import BaseModal from '@/components/common/BaseModal.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';
import EmptyState from '@/components/base/EmptyState.vue';

const { t } = useI18n();

const props = defineProps<{
  sessionId: number;
  autoLoad?: boolean;
}>();

const cuts = ref<any[]>([]);
const loading = ref(false);
const error = ref('');
const isExpanded = ref(false);
const selectedCut = ref<any>(null);

const loadCuts = async () => {
  if (cuts.value.length > 0 && isExpanded.value) {
    isExpanded.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = '';
    isExpanded.value = true;
    const response = await cashRegisterService.getAllCuts(props.sessionId);
    
    // Filter only daily_summary reports (cuts)
    const reports = Array.isArray(response) ? response : [];
    cuts.value = reports
      .filter((report: any) => report.report_type === 'daily_summary')
      .sort((a: any, b: any) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime());
  } catch (err: any) {
    error.value = err.response?.data?.error?.message || 'Error al cargar cortes';
    console.error('Error loading cuts:', err);
  } finally {
    loading.value = false;
  }
};

const getCutData = (cut: any) => {
  try {
    const data = typeof cut.data === 'string' ? JSON.parse(cut.data) : cut.data;
    // Agregar generated_at del cut principal al objeto de datos
    return {
      ...data,
      generated_at: cut.generated_at
    };
  } catch (e) {
    console.error('Error parsing cut data:', e);
    return {
      total_sales: 0,
      total_expenses: 0,
      total_transactions: 0,
      net_cash_flow: 0,
      cash_payments: 0,
      card_payments: 0,
      digital_payments: 0,
      other_payments: 0,
      generated_at: cut.generated_at
    };
  }
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

const viewCutDetails = (cut: any) => {
  selectedCut.value = cut;
};

onMounted(() => {
  if (props.autoLoad) {
    loadCuts();
  }
});
</script>
